import { supabase } from './supabase'
import { isDemoModeActive } from '../contexts/DemoContext'
import { demoImages, demoUser, addDemoImage, removeDemoImage } from '../mocks/demoData'

export interface TodoImage {
  id: string
  todo_id: string | null
  user_id: string
  image_url: string
  file_name: string
  file_size: number | null
  display_order: number
  created_at: string
}

const MAX_IMAGES_PER_TODO = 5
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB (全プラン共通)

// 許可される画像形式（セキュリティ対策）
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp']

/**
 * 画像ファイルのバリデーション
 */
function validateImageFile(file: File): { isValid: boolean; error?: string } {
  // 空ファイルチェック
  if (file.size === 0) {
    return { isValid: false, error: 'ファイルが空です' }
  }

  // MIMEタイプチェック
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: `この画像形式（${file.type || '不明'}）はサポートされていません。JPG、PNG、GIF、WebP形式の画像のみアップロード可能です`
    }
  }

  // 拡張子チェック
  const fileName = file.name.toLowerCase()
  const hasValidExtension = ALLOWED_IMAGE_EXTENSIONS.some(ext => fileName.endsWith(ext))
  if (!hasValidExtension) {
    return {
      isValid: false,
      error: 'ファイル拡張子が不正です。JPG、PNG、GIF、WebP形式の画像のみアップロード可能です'
    }
  }

  return { isValid: true }
}

/**
 * Upload an image for a todo
 */
export async function uploadTodoImage(
  todoId: string,
  file: File
): Promise<{ success: boolean; image?: TodoImage; error?: string }> {
  // デモモード中はローカル画像をData URLとして保存
  if (isDemoModeActive()) {
    try {
      // 画像ファイル検証
      const validation = validateImageFile(file)
      if (!validation.isValid) {
        return { success: false, error: validation.error }
      }

      // 現在の画像数チェック
      const existingImages = demoImages[todoId] || []
      if (existingImages.length >= MAX_IMAGES_PER_TODO) {
        return { success: false, error: `1つのタスクに添付できる画像は${MAX_IMAGES_PER_TODO}枚までです` }
      }

      // ファイルをData URLに変換
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました'))
        reader.readAsDataURL(file)
      })

      // デモ用画像オブジェクトを作成
      const newImage: TodoImage = {
        id: `demo-local-${Date.now()}-${Math.random().toString(36).substring(7)}`,
        todo_id: todoId,
        user_id: demoUser.id,
        image_url: dataUrl,
        file_name: file.name,
        file_size: file.size,
        display_order: existingImages.length,
        created_at: new Date().toISOString(),
      }

      // demoImagesに追加
      addDemoImage(todoId, newImage)

      return { success: true, image: newImage }
    } catch (error) {
      console.error('Demo upload error:', error)
      return { success: false, error: '画像の追加に失敗しました' }
    }
  }

  try {
    // セキュリティチェック: 画像ファイル検証
    const validation = validateImageFile(file)
    if (!validation.isValid) {
      console.warn('Image validation failed:', validation.error, 'File:', file.name, 'Type:', file.type)
      return { success: false, error: validation.error }
    }

    if (file.size > MAX_FILE_SIZE) {
      return { success: false, error: 'ファイルサイズは50MB以下にしてください' }
    }

    // Check current image count
    const { data: existingImages, error: countError } = await supabase
      .from('todo_images')
      .select('id')
      .eq('todo_id', todoId)

    if (countError) {
      console.error('Error checking image count:', countError)
      return { success: false, error: '画像数の確認に失敗しました' }
    }

    if (existingImages && existingImages.length >= MAX_IMAGES_PER_TODO) {
      return { success: false, error: `1つのタスクに添付できる画像は${MAX_IMAGES_PER_TODO}枚までです` }
    }

    // Get user ID
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'ログインが必要です' }
    }

    // Get todo and project info to determine the owner
    const { data: todo, error: todoError } = await supabase
      .from('todos')
      .select('project_id, projects(user_id, team_id)')
      .eq('id', todoId)
      .single()

    if (todoError || !todo) {
      console.error('Error fetching todo:', todoError)
      return { success: false, error: 'タスク情報の取得に失敗しました' }
    }

    // For team projects, use project owner's ID; otherwise use current user's ID
    const project = todo.projects as any
    const ownerId = project?.team_id ? project.user_id : user.id

    // Generate unique file name
    const fileExt = file.name.split('.').pop()
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`
    const filePath = `${ownerId}/${todoId}/${fileName}`

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from('task-images')
      .upload(filePath, file)

    if (uploadError) {
      console.error('Error uploading image:', uploadError)
      return { success: false, error: '画像のアップロードに失敗しました' }
    }

    // Get next display order
    const nextOrder = existingImages ? existingImages.length : 0

    // Save to database (use owner's ID for team projects)
    const { data: image, error: dbError } = await supabase
      .from('todo_images')
      .insert({
        todo_id: todoId,
        user_id: ownerId,
        image_url: filePath,
        file_name: file.name,
        file_size: file.size,
        display_order: nextOrder
      })
      .select()
      .single()

    if (dbError) {
      console.error('Error saving image to database:', dbError)
      // Rollback: delete uploaded file
      await supabase.storage.from('task-images').remove([filePath])
      return { success: false, error: '画像情報の保存に失敗しました' }
    }

    return { success: true, image }
  } catch (error) {
    console.error('Unexpected error uploading image:', error)
    return { success: false, error: '予期しないエラーが発生しました' }
  }
}

/**
 * Delete a todo image
 */
export async function deleteTodoImage(
  imageId: string
): Promise<{ success: boolean; error?: string }> {
  // デモモード中はdemoImagesから削除
  if (isDemoModeActive()) {
    // 画像のtodoIdを見つける
    for (const todoId of Object.keys(demoImages)) {
      const images = demoImages[todoId]
      const imageIndex = images.findIndex(img => img.id === imageId)
      if (imageIndex !== -1) {
        removeDemoImage(todoId, imageId)
        return { success: true }
      }
    }
    return { success: false, error: '画像が見つかりませんでした' }
  }

  try {
    // Get image info
    const { data: image, error: fetchError } = await supabase
      .from('todo_images')
      .select('*')
      .eq('id', imageId)
      .single()

    if (fetchError || !image) {
      console.error('Error fetching image:', fetchError)
      return { success: false, error: '画像情報の取得に失敗しました' }
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('task-images')
      .remove([image.image_url])

    if (storageError) {
      console.error('Error deleting from storage:', storageError)
      // Continue anyway to clean up database
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('todo_images')
      .delete()
      .eq('id', imageId)

    if (dbError) {
      console.error('Error deleting from database:', dbError)
      return { success: false, error: '画像の削除に失敗しました' }
    }

    return { success: true }
  } catch (error) {
    console.error('Unexpected error deleting image:', error)
    return { success: false, error: '予期しないエラーが発生しました' }
  }
}

/**
 * Get all images for a todo
 */
export async function getTodoImages(
  todoId: string
): Promise<{ success: boolean; images?: TodoImage[]; error?: string }> {
  // デモモード中はデモ用画像データを返す
  if (isDemoModeActive()) {
    const images = demoImages[todoId] || []
    return { success: true, images }
  }

  try {
    const { data: images, error } = await supabase
      .from('todo_images')
      .select('*')
      .eq('todo_id', todoId)
      .order('display_order', { ascending: true })

    if (error) {
      console.error('Error fetching todo images:', error)
      return { success: false, error: '画像の取得に失敗しました' }
    }

    return { success: true, images: images || [] }
  } catch (error) {
    console.error('Unexpected error fetching images:', error)
    return { success: false, error: '予期しないエラーが発生しました' }
  }
}

/**
 * Get signed URL for an image (valid for 1 hour)
 */
export async function getImageUrl(imagePath: string): Promise<string> {
  // デモモード中はpublicフォルダのパスまたはData URLをそのまま返す
  if (isDemoModeActive() && (imagePath.startsWith('/') || imagePath.startsWith('data:'))) {
    return imagePath
  }

  const { data, error } = await supabase.storage
    .from('task-images')
    .createSignedUrl(imagePath, 3600) // 1 hour expiry

  if (error) {
    console.error('Error creating signed URL:', error)
    return ''
  }

  return data.signedUrl
}

/**
 * Get optimized thumbnail URL using Supabase Image Transformation with signed URL
 * @param imagePath - Path to the image in storage
 * @param width - Thumbnail width (default: 240px for higher quality)
 * @param height - Thumbnail height (default: 240px for higher quality)
 * @param quality - Image quality 1-100 (default: 85 for better clarity)
 */
export async function getThumbnailUrl(
  imagePath: string,
  width: number = 240,
  height: number = 240,
  quality: number = 85
): Promise<string> {
  // デモモード中はpublicフォルダのパスまたはData URLをそのまま返す（変換なし）
  if (isDemoModeActive() && (imagePath.startsWith('/') || imagePath.startsWith('data:'))) {
    return imagePath
  }

  const { data, error } = await supabase.storage
    .from('task-images')
    .createSignedUrl(imagePath, 3600, {
      transform: {
        width,
        height,
        quality,
        resize: 'contain'
      }
    })

  if (error) {
    console.error('Error creating thumbnail URL:', error)
    return ''
  }

  return data.signedUrl
}

/**
 * Get optimized full-size URL using Supabase Image Transformation with signed URL
 * @param imagePath - Path to the image in storage
 * @param maxWidth - Maximum width (default: 1200px)
 * @param maxHeight - Maximum height (default: 1200px)
 * @param quality - Image quality 1-100 (default: 85)
 */
export async function getOptimizedImageUrl(
  imagePath: string,
  maxWidth: number = 1200,
  maxHeight: number = 1200,
  quality: number = 85
): Promise<string> {
  // デモモード中はpublicフォルダのパスまたはData URLをそのまま返す（変換なし）
  if (isDemoModeActive() && (imagePath.startsWith('/') || imagePath.startsWith('data:'))) {
    return imagePath
  }

  const { data, error } = await supabase.storage
    .from('task-images')
    .createSignedUrl(imagePath, 3600, {
      transform: {
        width: maxWidth,
        height: maxHeight,
        resize: 'contain', // アスペクト比を維持
        quality
      }
    })

  if (error) {
    console.error('Error creating optimized image URL:', error)
    return ''
  }

  return data.signedUrl
}

// URL Cache to avoid repeated signed URL requests
const urlCache = new Map<string, { url: string; expiresAt: number }>()

/**
 * Get cached signed URL (50 minute expiry)
 */
export async function getCachedImageUrl(imagePath: string): Promise<string> {
  const cached = urlCache.get(imagePath)
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url
  }

  const url = await getImageUrl(imagePath)
  urlCache.set(imagePath, {
    url,
    expiresAt: Date.now() + 50 * 60 * 1000 // 50 minutes
  })
  return url
}

/**
 * Get all images for the current user (for image management page)
 * Only returns images that belong to the user (user_id matches)
 * This excludes team project images where the user is just a member
 */
export async function getAllUserImages(): Promise<{
  success: boolean
  images?: (TodoImage & { todo_title?: string; project_name?: string })[]
  error?: string
}> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'ログインが必要です' }
    }

    // Get only images where user_id matches current user
    // This excludes team project images (which have the project owner's user_id)
    const { data: images, error: imagesError } = await supabase
      .from('todo_images')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })

    if (imagesError) {
      console.error('Error fetching user images:', imagesError)
      return { success: false, error: '画像の取得に失敗しました' }
    }

    if (!images || images.length === 0) {
      return { success: true, images: [] }
    }

    // Get unique todo IDs (filter out null values)
    const todoIds = [...new Set(images.map(img => img.todo_id).filter((id): id is string => id !== null))]

    // If no valid todo IDs, return images without todo info
    if (todoIds.length === 0) {
      return { success: true, images }
    }

    // Fetch todo information (without JOIN to avoid ambiguity)
    const { data: todos, error: todosError } = await supabase
      .from('todos')
      .select('id, title, project_id')
      .in('id', todoIds)

    if (todosError) {
      console.error('Error fetching todos:', todosError)
      // Still return images even if todos fetch fails
    }

    // Get unique project IDs
    const projectIds = [...new Set(todos?.map(todo => todo.project_id).filter(Boolean) || [])]

    // Fetch project information
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select('id, name')
      .in('id', projectIds)

    if (projectsError) {
      console.error('Error fetching projects:', projectsError)
      // Still return images even if projects fetch fails
    }

    // Create a map of project info
    const projectMap = new Map(
      projects?.map(project => [project.id, project.name]) || []
    )

    // Create a map of todo info
    const todoMap = new Map(
      todos?.map(todo => [
        todo.id,
        {
          title: todo.title,
          project_name: projectMap.get(todo.project_id)
        }
      ]) || []
    )

    // Merge data
    const enrichedImages = images.map(img => ({
      ...img,
      todo_title: todoMap.get(img.todo_id)?.title,
      project_name: todoMap.get(img.todo_id)?.project_name
    }))

    return { success: true, images: enrichedImages }
  } catch (error) {
    console.error('Unexpected error fetching user images:', error)
    return { success: false, error: '予期しないエラーが発生しました' }
  }
}

/**
 * Download an image file
 */
export async function downloadImage(imagePath: string, fileName: string): Promise<void> {
  // デモモード中はpublicフォルダから直接ダウンロード
  if (isDemoModeActive() && imagePath.startsWith('/')) {
    try {
      const response = await fetch(imagePath)
      if (!response.ok) {
        throw new Error('Failed to fetch image')
      }
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
      return
    } catch (error) {
      console.error('Demo download error:', error)
      throw new Error('ダウンロードに失敗しました')
    }
  }

  const url = await getImageUrl(imagePath)
  if (!url) {
    throw new Error('Failed to generate download URL')
  }

  try {
    // Fetch the file as blob to force download instead of opening in new tab
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error('Failed to fetch image')
    }

    const blob = await response.blob()

    // Create blob URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up blob URL
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Download error:', error)
    throw new Error('ダウンロードに失敗しました')
  }
}
